import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Award, Clock, Heart, Users, Zap, Moon, Brain, Flame, Droplets, Sparkles, CheckCircle } from 'lucide-react';

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const symptoms = [
    { icon: Zap, label: 'Constant Fatigue', description: 'Exhausted no matter how much you sleep' },
    { icon: Brain, label: 'Brain Fog', description: 'Trouble focusing or remembering things' },
    { icon: Flame, label: 'Weight Gain', description: 'Stubborn pounds that won\'t budge' },
    { icon: Moon, label: 'Poor Sleep', description: 'Tossing, turning, or waking up drenched' },
    { icon: Heart, label: 'Low Libido', description: 'Lost interest in intimacy' },
    { icon: Droplets, label: 'Night Sweats', description: 'Waking up soaked at 3am' },
    { icon: Sparkles, label: 'Mood Swings', description: 'Irritable, anxious, or just "off"' },
    { icon: Users, label: 'Hair Thinning', description: 'Noticing more hair in the drain' },
  ];

  const steps = [
    {
      number: '1',
      title: 'Book a Free Consult',
      description: 'A quick 15-minute call to discuss your symptoms and see if we\'re the right fit.',
    },
    {
      number: '2',
      title: 'Get Your Personalized Plan',
      description: 'Work with a hormone specialist to create a treatment plan tailored to your body.',
    },
    {
      number: '3',
      title: 'Feel Like Yourself Again',
      description: 'Start your treatment and finally get the energy, sleep, and clarity you deserve.',
    },
  ];

  const testimonials = [
    {
      quote: 'I lost 12 lbs and finally sleep through the night. For the first time in years, I wake up feeling rested.',
      name: 'Sarah M.',
      age: 47,
      outcome: 'Lost 12 lbs, sleeping better',
    },
    {
      quote: 'I thought feeling exhausted was just part of getting older. Within 6 weeks, my energy completely transformed.',
      name: 'Jennifer L.',
      age: 52,
      outcome: 'Energy restored',
    },
    {
      quote: 'My brain fog lifted and I stopped snapping at my family. I feel like myself again, the real me.',
      name: 'Michelle R.',
      age: 44,
      outcome: 'Mental clarity, improved mood',
    },
    {
      quote: 'After being dismissed by three doctors, someone finally listened. This changed my life.',
      name: 'Karen T.',
      age: 49,
      outcome: 'Finally heard',
    },
  ];

  const faqs = [
    {
      question: 'Is this covered by insurance?',
      answer: 'While we don\'t bill insurance directly, many patients use their HSA/FSA accounts. We also provide superbills you can submit for potential reimbursement. Our membership pricing is designed to be accessible and transparent, no surprise bills.',
    },
    {
      question: 'How fast will I see results?',
      answer: 'Most patients begin noticing improvements in energy and sleep within 2-4 weeks. Full hormone optimization typically takes 2-3 months as we fine-tune your treatment. Everyone\'s body is different, but we monitor your progress closely.',
    },
    {
      question: 'What does treatment involve?',
      answer: 'Treatment is personalized to your needs and may include bioidentical hormone therapy, targeted supplements, and lifestyle guidance. Everything is prescribed by licensed providers and delivered to your door. All consultations happen via secure video, no office visits required.',
    },
    {
      question: 'Is hormone therapy safe?',
      answer: 'When properly monitored by qualified providers, bioidentical hormone therapy has an excellent safety profile. We use evidence-based protocols, regular lab monitoring, and only FDA-approved medications. Your safety is our top priority.',
    },
    {
      question: 'Do I need labs first?',
      answer: 'We\'ll order comprehensive hormone labs as part of your care, you can get them done at a lab near you. If you have recent lab results (within 3 months), bring them to your consultation and we\'ll review them together.',
    },
    {
      question: 'What states do you serve?',
      answer: 'We currently provide telehealth services throughout Utah, with plans to expand. All consultations happen via secure video, and medications are delivered directly to your home.',
    },
  ];

  const scrollToBooking = () => {
    window.open('https://api.leadconnectorhq.com/widget/bookings/eligibility-next-steps-call', '_blank');
  };

  return (
    <div className="lp-page">
      {/* Minimal Header */}
      <header className="lp-header">
        <div className="lp-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
            alt="Glow Health Logo"
            className="brand-symbol"
          />
          <span>Glow Health</span>
        </div>
      </header>

      {/* Hero Section */}
      <section className="lp-hero">
        <div className="lp-hero-content">
          <p className="lp-hero-eyebrow">Telehealth Hormone Care for Women</p>
          <h1 className="lp-hero-headline">
            Tired of Being Told<br />It's "Just Stress"?
          </h1>
          <p className="lp-hero-subheadline">
            Balance your hormones. Reclaim your energy, your sleep, and yourself, <strong>100% online.</strong>
          </p>
          <button className="lp-cta-button" onClick={scrollToBooking}>
            Book Your Free Consultation
          </button>
          <p className="lp-hero-reassurance">
            No obligation. No judgment. Just answers.
          </p>
        </div>
        <div className="lp-hero-image">
          <div className="lp-hero-image-placeholder">
            <img 
              src="https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=600&h=700&fit=crop&crop=face" 
              alt="Woman feeling confident and healthy"
            />
          </div>
        </div>
      </section>

      {/* Symptom Validation Section */}
      <section className="lp-symptoms">
        <div className="lp-symptoms-content">
          <h2 className="lp-section-headline">Sound Familiar?</h2>
          <p className="lp-section-subtext">
            These aren't just "normal" signs of aging. They're signals your hormones need attention.
          </p>
          <div className="lp-symptoms-grid">
            {symptoms.map((symptom, index) => (
              <div key={index} className="lp-symptom-card">
                <div className="lp-symptom-icon">
                  <symptom.icon size={24} />
                </div>
                <h3 className="lp-symptom-label">{symptom.label}</h3>
                <p className="lp-symptom-description">{symptom.description}</p>
              </div>
            ))}
          </div>
          <p className="lp-symptoms-cta-text">
            If you're nodding along, you're not alone, and you don't have to keep feeling this way.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="lp-how-it-works">
        <div className="lp-how-content">
          <h2 className="lp-section-headline">How It Works</h2>
          <p className="lp-section-subtext">
            Getting started is simple. No waiting rooms. No runaround.
          </p>
          <div className="lp-steps">
            {steps.map((step, index) => (
              <div key={index} className="lp-step">
                <div className="lp-step-number">{step.number}</div>
                <div className="lp-step-content">
                  <h3 className="lp-step-title">{step.title}</h3>
                  <p className="lp-step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <button className="lp-cta-button lp-cta-secondary" onClick={scrollToBooking}>
            Book Your Free Consultation
          </button>
        </div>
      </section>

      {/* Trust & Credentials Section */}
      <section className="lp-trust">
        <div className="lp-trust-content">
          <h2 className="lp-section-headline">You're in Good Hands</h2>
          <div className="lp-trust-grid">
            <div className="lp-trust-item">
              <div className="lp-trust-icon">
                <Award size={28} />
              </div>
              <h3>Licensed Providers</h3>
              <p>Board-certified practitioners specializing in women's hormone health</p>
            </div>
            <div className="lp-trust-item">
              <div className="lp-trust-icon">
                <Shield size={28} />
              </div>
              <h3>HIPAA Compliant</h3>
              <p>Your health information is protected with enterprise-grade security</p>
            </div>
            <div className="lp-trust-item">
              <div className="lp-trust-icon">
                <Clock size={28} />
              </div>
              <h3>Convenient Telehealth</h3>
              <p>Video consultations from home, no traffic, no waiting rooms</p>
            </div>
            <div className="lp-trust-item">
              <div className="lp-trust-icon">
                <CheckCircle size={28} />
              </div>
              <h3>Evidence-Based Care</h3>
              <p>Treatments backed by research and tailored to your unique needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="lp-testimonials">
        <div className="lp-testimonials-content">
          <h2 className="lp-section-headline">Real Women. Real Results.</h2>
          <div className="lp-testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="lp-testimonial-card">
                <div className="lp-testimonial-quote">
                  "{testimonial.quote}"
                </div>
                <div className="lp-testimonial-author">
                  <span className="lp-testimonial-name">{testimonial.name}</span>
                  <span className="lp-testimonial-age">, {testimonial.age}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="lp-faq">
        <div className="lp-faq-content">
          <h2 className="lp-section-headline">Common Questions</h2>
          <div className="lp-faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`lp-faq-item ${openFaq === index ? 'open' : ''}`}
              >
                <button 
                  className="lp-faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {openFaq === index && (
                  <div className="lp-faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="lp-final-cta">
        <div className="lp-final-cta-content">
          <h2 className="lp-final-headline">
            You Don't Have to Feel<br />This Way Anymore
          </h2>
          <p className="lp-final-subtext">
            Take the first step toward feeling like yourself again. Your free consultation is waiting.
          </p>
          <button className="lp-cta-button lp-cta-light" onClick={scrollToBooking}>
            Book Your Free Consultation
          </button>
          <p className="lp-final-reassurance">
            15 minutes could change everything.
          </p>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-content">
          <div className="lp-footer-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
              style={{ width: '24px', height: '24px' }}
            />
            <span>Glow Health</span>
          </div>
          <p className="lp-footer-disclaimer">
            This is not a medical visit. Services are provided by licensed healthcare providers. 
            Results may vary. Glow Health LLC.
          </p>
          <div className="lp-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
